<script setup lang="ts">
  /**
   * Activity entry — 03-components/13g-activity-thread · OSCloud DS 1.1.134
   *
   * One entry of a `BfActivityThread`. Two kinds:
   *
   * - a **message**: an identity line (actor, role badge, time) over a `card--details` box
   *   holding the words and, optionally, an attachments strip;
   * - an **event** (`event`): no box — a tone marker and one line of secondary copy.
   *
   * `counterpart` marks a message the reader did not write; its box takes the faintest orange
   * wash so who wrote it is read before the words are.
   */
  withDefaults(
    defineProps<{
      /** Who wrote it. Omitted on an event entry, whose actor is named inside the summary. */
      actor?: string
      /** The stamp as it should read. */
      time?: string
      /** Machine-readable stamp for `<time datetime>`; without it the stamp is a plain span. */
      datetime?: string
      /** A message the reader did not write — the wash says so. */
      counterpart?: boolean
      /** Event form: a marker and a summary line instead of a message box. */
      event?: boolean
      /** The event marker's tone. Neutral when omitted. */
      marker?: 'neutral' | 'success' | 'error' | 'warning' | 'information'
    }>(),
    { actor: '', time: '', datetime: undefined, counterpart: false, event: false, marker: 'neutral' },
  )
</script>

<template>
  <li
    class="activity-thread__entry"
    :class="{
      'activity-thread__entry--counterpart': counterpart,
      'activity-thread__entry--event': event,
    }"
  >
    <div class="activity-thread__meta">
      <span
        v-if="event"
        class="activity-thread__marker"
        :class="marker !== 'neutral' ? `activity-thread__marker--${marker}` : null"
        aria-hidden="true"
      />
      <span v-if="actor" class="activity-thread__actor">{{ actor }}</span>
      <span v-if="$slots.role" class="activity-thread__role"><slot name="role" /></span>
      <p v-if="event" class="activity-thread__summary"><slot name="summary" /></p>
      <time v-if="time && datetime" class="activity-thread__time" :datetime="datetime">{{ time }}</time>
      <span v-else-if="time" class="activity-thread__time">{{ time }}</span>
    </div>

    <!-- An event is the meta line and nothing else: no box, because there is nothing to read
         inside one. A message's words, and anything it carries, live in the details box. -->
    <div v-if="!event" class="activity-thread__box">
      <slot />
      <div v-if="$slots.attachments" class="activity-thread__attachments">
        <slot name="attachments" />
      </div>
    </div>
  </li>
</template>

<style scoped lang="scss">
  .activity-thread__entry {
    display: flex;
    flex-direction: column;
    gap: var(--bf-sp-2);
    min-width: 0;
  }

  /* ─── Identity line: actor, role, time ──────────────────────────── */
  .activity-thread__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--bf-sp-1) var(--bf-sp-2);
    min-width: 0;
  }

  .activity-thread__actor {
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-xs_5);
    font-weight: var(--bf-fw-semibold);
    line-height: var(--bf-lh-snug);
    color: var(--bf-fg-1);
  }

  .activity-thread__role {
    display: inline-flex;
    align-items: center;
  }

  /* The time takes the trailing edge of the line, on the tertiary step — the stamp is the one
     thing here nobody reads on purpose, and it is also in the entry's `datetime`. */
  .activity-thread__time {
    margin-left: auto;
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-xs);
    line-height: var(--bf-lh-snug);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    color: var(--bf-fg-3);
  }

  /* ─── Event entry ───────────────────────────────────────────────── */
  .activity-thread__summary {
    min-width: 0;
    margin: 0;
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-sm);
    line-height: var(--bf-lh-snug);
    color: var(--bf-fg-2);
  }

  .activity-thread__summary :deep(strong) {
    font-weight: var(--bf-fw-semibold);
    color: var(--bf-fg-1);
  }

  /* A tone dot. Neutral by default; the tones are the notification accents. */
  .activity-thread__marker {
    flex: 0 0 auto;
    width: var(--bf-activity-thread-marker-size, 10px);
    height: var(--bf-activity-thread-marker-size, 10px);
    border-radius: var(--bf-r-pill);
    background: var(--bf-dusky-2);
  }

  .activity-thread__marker--success {
    background: var(--bf-success);
  }

  .activity-thread__marker--error {
    background: var(--bf-error-accent);
  }

  .activity-thread__marker--warning {
    background: var(--bf-warning-soft-text);
  }

  .activity-thread__marker--information {
    background: var(--bf-info-accent);
  }

  /* ─── The message box ───────────────────────────────────────────────
     The details surface (13a) restated here rather than composed from BfCard, because the box
     is the entry's own anatomy: the attachments strip is its last child and the counterpart
     wash is set on it. */
  .activity-thread__box {
    padding: 18px 22px;
    border: 1px solid var(--bf-line);
    border-radius: var(--bf-r-lg);
    background: var(--bf-white);
  }

  .activity-thread__entry--counterpart .activity-thread__box {
    background: var(--bf-activity-thread-counterpart-surface, var(--bf-warning-surface));
  }

  /* Siblings inside the box — text, table, attachments — keep one rhythm. */
  .activity-thread__box > :deep(* + *) {
    margin-top: var(--bf-sp-3);
  }

  /* The words. 14px, the block's sentence rank: everything else inside the box a message sits
     in is 13–14px, and 16px gave the body a heading rank it does not hold. The relaxed leading
     stays — it is what makes a paragraph at this size readable. Reached through :deep() because
     a message's words are the consumer's markup, not this block's. */
  .activity-thread__box :deep(.activity-thread__text) {
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-sm);
    line-height: var(--bf-lh-relaxed);
    overflow-wrap: anywhere;
    color: var(--bf-fg-1);
  }

  .activity-thread__box :deep(.activity-thread__text > *) {
    margin: 0;
  }

  .activity-thread__box :deep(.activity-thread__text > * + *) {
    margin-top: var(--bf-sp-2);
  }

  .activity-thread__box :deep(.activity-thread__text ul),
  .activity-thread__box :deep(.activity-thread__text ol) {
    padding-left: 1.4em;
  }

  /* Attachments close the box: a hairline strip of muted links, wrapping. It sits inside the
     box's padding, so it is a strip rather than a footer bar. */
  .activity-thread__attachments {
    display: flex;
    flex-wrap: wrap;
    gap: var(--bf-sp-1) var(--bf-sp-3);
    padding-top: var(--bf-sp-2);
    border-top: 1px solid var(--bf-grey-light);
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-xs_5);
  }

  @media (max-width: 600px) {
    /* --bf-bp-sm: the stamp keeps the line rather than being pushed off it. */
    .activity-thread__time {
      flex: 1 1 auto;
      margin-left: 0;
    }
  }
</style>
